import { useEffect, type CSSProperties, type ReactNode } from 'react';

import { cn } from '../lib/cn.js';

/** Imagem de fundo default usada em todas as telas de auth. Servida de
 * `apps/web/public/` — quem embeddar o AuthShell precisa disponibilizar a
 * imagem nesse path. Passe `''` em `backgroundImageSrc` para desabilitar. */
const DEFAULT_BACKGROUND_IMAGE_SRC = '/fundo-paginas-login-logout.png';

export interface AuthShellProps {
  /** Marca da plataforma. Renderizada no topo, centralizada. */
  brand: ReactNode;
  /** Conteúdo principal (formulário de login/signup/MFA). */
  children: ReactNode;
  /** Rodapé opcional (ex: links "Esqueci minha senha", "Política de privacidade"). */
  footer?: ReactNode;
  /** Classe extra no card central. */
  cardClassName?: string;
  /**
   * URL de imagem de fundo full-bleed. Default:
   * `/fundo-paginas-login-logout.png`. Passe `''` para desabilitar o fundo
   * (o card volta a ser sólido com tokens default).
   *
   * Quando há fundo, a imagem é coberta por um overlay com gradiente
   * brand/sidebar semi-transparente que mantém legibilidade do card em ambos
   * os temas (light/dark). O card fica **translúcido com blur** para
   * preservar a profundidade visual sem esconder a foto.
   */
  backgroundImageSrc?: string;
}

/**
 * Shell para telas de autenticação (login, signup, MFA, reset de senha).
 * Layout centralizado mobile-first, ocupa `min-h-dvh` com safe-area.
 * Por padrão renderiza uma imagem de fundo coberta por um overlay navy
 * gradient — use `<Brand onDark />` no `brand` acima do overlay.
 *
 * @example
 * // Fundo default ligado:
 * <AuthShell brand={<Brand onDark />}>
 *   <LoginForm />
 * </AuthShell>
 *
 * @example
 * // Sem fundo (card sólido):
 * <AuthShell brand={<Logo />} backgroundImageSrc="">
 *   <LoginForm />
 * </AuthShell>
 */
export function AuthShell({
  brand,
  children,
  footer,
  cardClassName,
  backgroundImageSrc = DEFAULT_BACKGROUND_IMAGE_SRC,
}: AuthShellProps) {
  const hasBackground = Boolean(backgroundImageSrc);

  // As telas de auth são desenhadas para o LIGHT MODE (overlay navy +
  // card glass calibrados sobre fundo claro). Quando o usuário faz
  // logout estando no dark, o `data-theme="dark"` global desconfigura
  // os tokens (card vira navy, contraste do texto quebra etc).
  // Solução: forçar `data-theme="light"` no <html> enquanto o AuthShell
  // estiver montado, restaurando o tema anterior ao desmontar. Não toca
  // no localStorage — preferência do usuário fica preservada para
  // quando ele entrar de volta no app.
  useEffect(() => {
    const html = document.documentElement;
    const previousTheme = html.getAttribute('data-theme');
    html.setAttribute('data-theme', 'light');
    return () => {
      if (previousTheme !== null) html.setAttribute('data-theme', previousTheme);
      else html.removeAttribute('data-theme');
    };
  }, []);

  return (
    <div
      className={cn(
        'relative flex min-h-dvh flex-col bg-background text-foreground',
        'px-4 py-8 sm:px-6 lg:px-8',
        'pt-[max(2rem,env(safe-area-inset-top))]',
        'pb-[max(2rem,env(safe-area-inset-bottom))]',
      )}
    >
      {hasBackground ? (
        <>
          {/* Camada 1: imagem nítida — protagonismo no fundo. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${backgroundImageSrc})` }}
          />
          {/* Camada 2: overlay navy gradient — opacidade calibrada para
              criar atmosfera escura legível por baixo do card glass.
              Light: /65 /50 /70 (navy moderado-forte). Dark: /80 /65
              /85 (mais opaco — overlay navy sobre fundo navy precisa
              de mais "peso" para a imagem virar textura sutil). */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-sidebar/65 via-sidebar/50 to-sidebar/70 dark:from-sidebar/80 dark:via-sidebar/65 dark:to-background/85"
          />
        </>
      ) : null}

      <div className="relative mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center gap-6">
        <div className="flex w-full items-center justify-center">{brand}</div>
        <div
          // Quando há imagem de fundo, sobrescreve --foreground e
          // --muted-foreground LOCALMENTE no card glass para tons
          // escuros (preto/grafite) — garante contraste de todos os
          // textos filhos (subtitle, links, hints) sobre o fundo
          // translúcido sem precisar tocar em cada componente.
          style={
            hasBackground
              ? ({
                  '--foreground': '0 0% 8%',
                  '--muted-foreground': '0 0% 18%',
                } as CSSProperties)
              : undefined
          }
          className={cn(
            'w-full rounded-lg border p-6 sm:p-8',
            hasBackground
              ? 'border-border/15 bg-card/15 text-foreground shadow-2xl backdrop-blur-md'
              : 'border-border bg-card text-card-foreground shadow-sm',
            cardClassName,
          )}
        >
          {children}
        </div>
        {footer ? (
          <div
            className={cn(
              'w-full text-center text-sm',
              hasBackground ? 'text-sidebar-foreground/90' : 'text-muted-foreground',
            )}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
